import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import waypointBg from '@ibk/shared-assets/icons/waypoint-background-client.svg';
import loginBg from '@ibk/shared-assets/images/hero-login-dominioempresa.png';
import { getPartyIdFromToken, saveAccountId, saveAllAccounts, usePersonAccess } from '@ibk/shared-auth';
import { resetAllApiCaches, selectAccessToken, selectIsAnyProductLoading, setSelectedAccountId, useAppDispatch, useAppSelector, useGetProductRelationShips, useHomeProducts, } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ignoredRoutes, useGetHomePermissions } from '@ibk/shared-permissioning';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { ApiProductStatus, FeatureFlagUseCase, ProductAlias } from '@ibk/shared-types';
import { Loading, LoadingScreen, CompanySelection as SharedCompanySelection, useCnpjAlphanumeric, } from '@ibk/shared-ui';
import { getAccountIdentifierFormatted, getAccountName, getLastAccessedCompany, saveLastAccessedCompany, sortCompaniesByLastAccessed, } from '@ibk/shared-utils';
import { dominioempresaTranslations } from '../../translations';
const CompanySelectionContent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { data, loading, error, refetch } = usePersonAccess();
    const { saveBillingProduct } = useGetProductRelationShips();
    const { getBillingProduct } = useGetProductRelationShips();
    const hasProductsLoading = useAppSelector(selectIsAnyProductLoading);
    const [fetchingValidations, setFetchingValidations] = useState(false);
    const [selectedCompanyOwnerPartyId, setSelectedCompanyOwnerPartyId] = useState(null);
    const lastAccessedCompanyId = getLastAccessedCompany();
    const { finishedValidations, hasAccountPjPermission, hasBenefitPermission, hasBillingPermission, hasDigitalAccountingPermission, hasClientPortalViewPermission, } = useGetHomePermissions();
    const { products, isLoading } = useHomeProducts({
        enabled: !!fetchingValidations,
    });
    const { isFeatureFlagWhiteListed, getFeatureFlags, clearCache } = useFeatureFlag(undefined, {
        autoLoad: !ignoredRoutes.includes(location.pathname),
    });
    const { isAlphanumericEnabled, maskCnpj: maskCnpjAlpha, isValidCnpj: isValidCnpjAlpha } = useCnpjAlphanumeric();
    const hasZestraFlag = isFeatureFlagWhiteListed(FeatureFlagUseCase.NEW_IBKS_ESCRITORIO_MODULE_BILLING);
    const accessToken = useAppSelector(selectAccessToken);
    const partyId = accessToken ? (getPartyIdFromToken(accessToken) ?? undefined) : undefined;
    const isAuthorizationOwner = !!partyId && !!selectedCompanyOwnerPartyId && partyId === selectedCompanyOwnerPartyId;
    const shouldShowAllMenuItems = hasClientPortalViewPermission === true && (!selectedCompanyOwnerPartyId || isAuthorizationOwner);
    const getUserAccesses = () => {
        const storedProduct = getBillingProduct();
        const clientBillingProduct = products[ProductAlias.COBRANCAS];
        const isClientBillingContracted = !clientBillingProduct.isLoading && clientBillingProduct.data?.status === ApiProductStatus.ACTIVE;
        const clientContabilidadeDigitalProduct = products[ProductAlias.CONTABILIDADE_DIGITAL];
        const isClientContabilidadeDigitalActive = !clientContabilidadeDigitalProduct.isLoading &&
            clientContabilidadeDigitalProduct.data?.status === ApiProductStatus.ACTIVE;
        const permissions = {
            hasAccountPj: hasAccountPjPermission,
            hasDigitalAccounting: hasDigitalAccountingPermission && isClientContabilidadeDigitalActive,
            hasClientCenter: !!hasClientPortalViewPermission,
            hasBenefit: hasBenefitPermission,
            hasBilling: hasZestraFlag && hasBillingPermission && isClientBillingContracted && !!storedProduct,
        };
        const permissionValues = Object.values(permissions).filter(Boolean);
        const hasTwoOrMoreAccesses = shouldShowAllMenuItems || !permissionValues.length || permissionValues.length >= 2;
        return { permissions, hasTwoOrMoreAccesses };
    };
    useEffect(() => {
        refetch();
    }, []);
    useEffect(() => {
        const runValidationNav = () => {
            const isClientPortalPermissionResolved = hasClientPortalViewPermission !== undefined;
            if (fetchingValidations &&
                finishedValidations &&
                !hasProductsLoading &&
                !isLoading &&
                isClientPortalPermissionResolved) {
                const { permissions, hasTwoOrMoreAccesses } = getUserAccesses();
                switch (true) {
                    case hasTwoOrMoreAccesses:
                        navigate('/home');
                        break;
                    case permissions.hasAccountPj:
                        navigate('/conta-pj');
                        break;
                    case permissions.hasDigitalAccounting:
                        navigate('/contabilidade-digital');
                        break;
                    case permissions.hasBenefit:
                        navigate('/beneficios');
                        break;
                    case permissions.hasBilling:
                        navigate('/cobrancas');
                        break;
                    case permissions.hasClientCenter:
                        navigate('/contabilidade-digital');
                        break;
                    default:
                        navigate('/home');
                        break;
                }
                setFetchingValidations(false);
            }
        };
        const delayDebounceFn = setTimeout(() => {
            runValidationNav();
        }, 600);
        return () => clearTimeout(delayDebounceFn);
    }, [
        fetchingValidations,
        finishedValidations,
        hasProductsLoading,
        isLoading,
        hasClientPortalViewPermission,
        shouldShowAllMenuItems,
    ]);
    const companies = React.useMemo(() => {
        if (!data || !Array.isArray(data))
            return [];
        const mappedCompanies = data.map(item => {
            const documentNumber = item.entity.documentNumber;
            const cnpj = isAlphanumericEnabled && isValidCnpjAlpha(documentNumber)
                ? maskCnpjAlpha(documentNumber)
                : getAccountIdentifierFormatted(documentNumber);
            return {
                cnpj,
                name: getAccountName(item),
                id: item.targetId,
            };
        });
        return sortCompaniesByLastAccessed(mappedCompanies, lastAccessedCompanyId);
    }, [data, lastAccessedCompanyId, isAlphanumericEnabled, isValidCnpjAlpha, maskCnpjAlpha]);
    const handleSelectCompany = async (companyId) => {
        setSelectedCompanyOwnerPartyId(data?.find(item => item.targetId === companyId)?.authorizationOwnerPartyId ?? null);
        dispatch(setSelectedAccountId(companyId));
        resetAllApiCaches(dispatch);
        if (data) {
            saveAccountId(data, companyId);
            saveAllAccounts(data);
            await saveBillingProduct(companyId);
        }
        saveLastAccessedCompany(companyId);
        const flagsResult = await getFeatureFlags(true);
        if (!flagsResult.featureFlags?.length) {
            clearCache();
        }
        setFetchingValidations(companyId);
    };
    const handleGoBack = () => {
        const cameFromAuthRoutes = ['two-factor-verify', 'auth-flow'];
        const cameFromAuth = cameFromAuthRoutes.includes(location.state?.from);
        if (cameFromAuth) {
            navigate('/login', { replace: true });
        }
        else if (window.history.length > 1) {
            navigate(-1);
        }
        else {
            navigate('/login');
        }
    };
    if (fetchingValidations) {
        return _jsx(Loading, { fullScreen: true, "data-testid": 'company-selection-loading-permissions' });
    }
    if (loading) {
        return _jsx(LoadingScreen, { "data-testid": 'company-selection-loading-company' });
    }
    if (error) {
        return (_jsxs("div", { "data-testid": 'company-selection-error-company', style: {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
            }, children: [_jsxs("div", { "data-testid": 'company-selection-error-message-company', style: { color: 'red', marginBottom: '1rem' }, children: ["Erro: ", error] }), _jsx("button", { "data-testid": 'company-selection-retry-button-company', onClick: () => window.location.reload(), children: "Tentar novamente" })] }));
    }
    return (_jsx(SharedCompanySelection, { "data-testid": 'client-company-selection-office', companies: companies, loginBg: loginBg, waypointBg: waypointBg, logoType: 'servicosdigitais-white', onSelectCompany: handleSelectCompany, onGoBack: handleGoBack, initialCompanyId: lastAccessedCompanyId || undefined, title: t('company-selection.title'), submitButtonText: t('company-selection.submit-button'), backButtonText: t('company-selection.back-button') }));
};
export default function CompanySelection() {
    return (_jsx(TranslationProvider, { translations: dominioempresaTranslations, defaultLanguage: 'pt-BR', children: _jsx(CompanySelectionContent, {}) }));
}
